import { Component, Input, Output, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import { DateUtils } from "../utils/date.utils";
import {
    SearchVariant,
    SearchProduct,
    SUPPLY_TYPE_PRE_ORDER,
    DEFAULT_MOQ,
    DEFAULT_QUANTITY,
} from "../types/variant.types";
import { AVAILABILITY_TEXT } from "../constants/availability.constants";
import { NotificationService } from "../../core/providers/notification/notification.service";
import {
    getMoq,
    getMinQ,
    getMaxQ,
    getQuantityInfoText,
    hasMinQ,
    getOrderStep,
    getBundleLabel,
    getInitialQuantity,
    normalizePackageQuantity,
} from "../utils/pricing.utils";
import { filterVariantsBySupplyTypes } from "../../common/utils/supply-type-filter.utils";
import {
    filterVariantsWithValidSupplier,
    isValidSupplierId,
} from "../../common/utils/variant-offer.utils";

@Component({
    selector: "vsf-product-table",
    templateUrl: "./product-table.component.html",
    styleUrls: ["./product-table.component.scss"],
})
export class ProductTableComponent {
    readonly TEXT = AVAILABILITY_TEXT;

    @Input() product: SearchProduct;
    @Input() supplierId: string | null = null;
    @Input() supplierDeliveryTerm: string | null = null;
    @Input() supplyTypes: string[] = [];
    @Input() isLoggedIn: boolean | null = true;
    @Input() suppressGuestCellBlur = false;
    @Output() addToCart = new EventEmitter<{
        productId: string;
        variantId: string;
        quantity: number;
    }>();

    get guestCellBlur(): boolean {
        return this.isLoggedIn !== true && !this.suppressGuestCellBlur;
    }

    itemQuantities: { [key: string]: number } = {};
    showAllOffersMap: { [productId: string]: boolean } = {};

    constructor(
        private router: Router,
        private notificationService: NotificationService
    ) {}

    getShippingDateDisplay = (variant: SearchVariant): string =>
        DateUtils.getVariantShippingDateDisplay(
            variant,
            this.supplierDeliveryTerm
        );

    isPreOrder = (variant: SearchVariant): boolean =>
        variant?.supplyType === SUPPLY_TYPE_PRE_ORDER;

    getAvailabilityDisplay = (variant: SearchVariant): string => {
        if (this.isPreOrder(variant)) {
            return AVAILABILITY_TEXT.PRE_ORDER;
        }
        const qty = variant?.qty ?? DEFAULT_QUANTITY;
        if (qty <= 0) {
            return AVAILABILITY_TEXT.OUT_OF_STOCK;
        }
        return `${qty} ${AVAILABILITY_TEXT.UNITS_SUFFIX}`;
    };

    getMoq = getMoq;
    getMinQ = getMinQ;
    getMaxQ = getMaxQ;
    getQuantityInfoText = getQuantityInfoText;
    hasMinQ = hasMinQ;
    getBundleLabel = getBundleLabel;

    private getVariantById = (variantId: string): SearchVariant | undefined => {
        return this.getVisibleVariants().find((v) => v.id === variantId);
    };

    private getMoqByVariantId = (variantId: string): number => {
        const variant = this.getVariantById(variantId);
        return variant ? this.getMoq(variant) : DEFAULT_MOQ;
    };

   
    showOfferForVariant(variant: SearchVariant | null | undefined): boolean {
        if (!variant || !isValidSupplierId(variant.supplierId) || (variant.qty ?? 0) < 0) {
            return false;
        }
        if (this.isLoggedIn !== true) {
            return true;
        }
        return (variant.priceWithTax ?? 0) > 0;
    }

    showPrimaryOfferRow(): boolean {
        return this.showOfferForVariant(this.getDisplayVariant());
    }

    getVisibleVariants = (): SearchVariant[] => {
        let variants = filterVariantsWithValidSupplier(this.product?.variants ?? []);
        if (this.supplyTypes.length > 0) {
            variants = filterVariantsBySupplyTypes(variants, this.supplyTypes);
        }
        return variants;
    };

    isValidSupplierId = isValidSupplierId;

    getDisplayVariant = (): SearchVariant | null => {
        const variants = this.getVisibleVariants();
        if (!variants.length) {
            return null;
        }

        if (!this.supplierId) {
            return variants.reduce<SearchVariant | null>(
                (min, current) =>
                    !min || current.priceWithTax < min.priceWithTax ? current : min,
                null,
            );
        }

        const supplierVariant = variants.find(
            (variant) =>
                variant?.supplierId?.toString() === this.supplierId?.toString(),
        );
        if (supplierVariant) {
            return supplierVariant;
        }

        return variants.reduce<SearchVariant | null>(
            (min, current) =>
                !min || current.priceWithTax < min.priceWithTax ? current : min,
            null,
        );
    };

    onAddToCart = (
        productId: string,
        variantId: string,
        quantity: number
    ): void => {
        this.addToCart.emit({ productId, variantId, quantity });
    };

    toggleShowAllOffers = (product: SearchProduct): void => {
        const productId = product.productId;
        this.showAllOffersMap[productId] = !this.showAllOffersMap[productId];
    };

    isShowingAllOffers = (product: SearchProduct): boolean =>
        !!this.showAllOffersMap[product.productId];

    getQuantityKey = (productId: string, variantId: string): string =>
        `${productId}_${variantId}`;

    increaseQuantity = (productId: string, variantId: string): void => {
        const key = this.getQuantityKey(productId, variantId);
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const maxQ = this.getMaxQ(variant);
        const currentQty = this.getQuantity(productId, variantId);
        const incrementStep = getOrderStep(variant, this.product);

        if (maxQ && currentQty >= maxQ) {
            return;
        }

        this.itemQuantities[key] = currentQty + incrementStep;
    };

    decreaseQuantity = (productId: string, variantId: string): void => {
        const key = this.getQuantityKey(productId, variantId);
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const minQ = this.getMinQ(variant);
        const currentQty = this.getQuantity(productId, variantId);
        const decrementStep = getOrderStep(variant, this.product);
        const newQty = currentQty - decrementStep;

        if (newQty >= minQ) {
            this.itemQuantities[key] = newQty;
        } else {
            this.showMoqAlert(minQ);
        }
    };

    private showMoqAlert = (moq: number): void => {
        this.notificationService
            .error(`${AVAILABILITY_TEXT.MOQ_ALERT} ${moq}`)
            .subscribe();
    };

    onQuantityChange = (
        value: string,
        productId: string,
        variantId: string
    ): void => {
        const key = this.getQuantityKey(productId, variantId);
        const variant = this.getVariantById(variantId);
        if (!variant) return;
        const minQ = this.getMinQ(variant);
        const quantity = parseInt(value, 10);

        if (!isNaN(quantity) && quantity >= minQ) {
            this.itemQuantities[key] = normalizePackageQuantity(
                quantity,
                variant,
                this.product,
            );
        } else {
            this.showMoqAlert(minQ);
            this.itemQuantities[key] = getInitialQuantity(variant, this.product);
        }
    };

    getQuantity = (productId: string, variantId: string): number => {
        const key = this.getQuantityKey(productId, variantId);
        const variant = this.getVariantById(variantId);
        if (!variant) return DEFAULT_MOQ;
        if (this.itemQuantities[key] === undefined) {
            this.itemQuantities[key] = getInitialQuantity(variant, this.product);
        }
        return this.itemQuantities[key];
    };

    goToSupplier = (supplierId: string): void => {
        this.router.navigate([`/category/products/${supplierId}`]);
    };
}
